import { useEffect, useMemo, useState } from 'react';
import { getDomainDisplayName } from '@app/shared/utils/domainDisplay.utils';
import { Link, useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudArrowUp,
  faLayerGroup,
  faMicrochip,
  faChevronDown,
  faFileLines,
  faPen,
  faTrashCan
} from '@fortawesome/free-solid-svg-icons';
import FeatherIcon from 'feather-icons-react';
import { Project } from '@app/shared/types/project.type';
import { useDispatch } from 'react-redux';
import { setToast } from 'components/Toasts/data-access/toast-reducer';
import { fetchMTCPlusProjectById } from './data-access/mtc-plus.api';
import AudioTab from 'pages/project-management/project-details/Audio/AudioTab';
import InventoryTab from 'components/tabs/InventoryTab';
import { useMainLayoutContext } from 'providers/MainLayoutProvider';
import 'pages/csa-plus/ProjectDetails.css';

type NavItem = 'summary' | 'audio-upload' | 'deep-fake';

const MTCPlusProjectDetails = () => {
  const { setContentClass } = useMainLayoutContext();
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch();
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeNavItem, setActiveNavItem] = useState<NavItem>('summary');
  const [uploadsExpanded, setUploadsExpanded] = useState(true);
  const [dimensionsExpanded, setDimensionsExpanded] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setError('Project ID is missing');
      setIsLoading(false);
      return;
    }

    localStorage.setItem('projectId', projectId);
    const userId = localStorage.getItem('userId') || undefined;
    setIsLoading(true);
    setError(null);

    fetchMTCPlusProjectById(projectId, userId)
      .then(data => {
        setProject(data);
        if (!data) setError('Project not found');
      })
      .catch(err => {
        console.error('Failed to fetch MTC+ project:', err);
        setError('Failed to load project');
        setProject(null);
      })
      .finally(() => setIsLoading(false));
  }, [projectId]);

  useEffect(() => {
    setContentClass('px-0 pt-navbar');
    return () => setContentClass('');
  }, [setContentClass]);

  const userId = project?.userId || localStorage.getItem('userId') || '';
  const uploadsObject = (project as any)?.uploads ?? {};

  const showYetToBeImplementedToast = (action: 'Edit' | 'Delete') => {
    dispatch(
      setToast({
        show: true,
        title: `${action} project`,
        message: 'Yet to be implemented for MTC+.'
      })
    );
  };

  const uploadSummary = useMemo(
    () =>
      Object.entries(uploadsObject)
        .filter(([, value]) => Array.isArray(value))
        .map(([key, value]) => ({
          key,
          count: (value as unknown[]).length
        })),
    [uploadsObject]
  );

  const gradientStyle = {
    minHeight: '100vh',
    width: '100%',
    background: 'linear-gradient(293deg, rgb(239 243 255) 0%, rgb(255 255 255) 100%)',
    backgroundAttachment: 'fixed' as const,
    padding: '24px 16px',
    position: 'relative' as const
  };

  if (isLoading) {
    return (
      <div
        style={{
          ...gradientStyle,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress size={50} />
        <p className="text-muted mt-2">Loading project...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div style={gradientStyle}>
        <div className="py-5 text-center">
          <p className="text-danger mb-3">{error || 'Project not found'}</p>
          <Link to="/mtc+/all-projects" className="btn btn-outline-primary">
            Back to All Projects
          </Link>
        </div>
      </div>
    );
  }

  const renderMainContent = () => {
    if (activeNavItem === 'audio-upload') {
      return (
        <div className="csa-content-card" style={{ padding: '24px 32px' }}>
          <InventoryTab
            audioOnly
            projectForUpload={{
              projectId: project.projectId,
              domain: project.domain
            }}
          />
        </div>
      );
    }

    if (activeNavItem === 'deep-fake') {
      return <AudioTab projectId={project.projectId} userId={userId} />;
    }

    return (
      <div className="csa-content-card" style={{ padding: '24px 32px' }}>
        {project.domain && (
          <div className="d-flex align-items-center gap-2 text-body-secondary mb-3">
            <FeatherIcon icon="briefcase" size={16} />
            <span>{getDomainDisplayName(project.domain)}</span>
          </div>
        )}

        <h6 className="text-uppercase text-body-tertiary fw-bold mb-2">Overview</h6>
        <p className="text-body-secondary">{project.overview || 'No description available.'}</p>

        <h6 className="text-uppercase text-body-tertiary fw-bold mb-2 mt-4">Uploads</h6>
        {uploadSummary.length ? (
          <div className="d-flex flex-wrap gap-2">
            {uploadSummary.map(item => (
              <span key={item.key} className="badge bg-light text-dark border">
                {item.key.replace(/_/g, ' ')}: {item.count}
              </span>
            ))}
          </div>
        ) : (
          <p className="mb-0 text-body-secondary">No upload metadata available yet.</p>
        )}

        {project.projectId && (
          <div className="mt-3">
            <small className="text-muted">Project ID: {project.projectId}</small>
          </div>
        )}
      </div>
    );
  };

  const sidebarWidth = 280;

  return (
    <div className="csa-project-details" style={gradientStyle}>
      <aside
        className="csa-sidebar"
        style={{
          position: 'fixed',
          left: 0,
          top: 'var(--phoenix-navbar-top-height, 72px)',
          bottom: 0,
          width: sidebarWidth,
          backgroundColor: '#fff',
          borderRight: '1px solid #e5e7eb',
          boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
          zIndex: 90,
          overflowY: 'auto',
          padding: '20px 12px'
        }}
      >
        <nav className="d-flex flex-column gap-0">
          <div
            className={`csa-nav-item ${activeNavItem === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveNavItem('summary')}
          >
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon
                icon={faFileLines}
                size="sm"
                className={activeNavItem === 'summary' ? 'text-primary' : 'text-muted'}
              />
              <span className="fw-semibold" style={{ fontSize: '14px' }}>
                Summary
              </span>
            </div>
          </div>

          <div>
            <div
              className={`csa-nav-item ${activeNavItem === 'audio-upload' ? 'active' : ''}`}
              onClick={() => setUploadsExpanded(!uploadsExpanded)}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faCloudArrowUp}
                    size="sm"
                    className={activeNavItem === 'audio-upload' ? 'text-primary' : 'text-muted'}
                  />
                  <span className="fw-semibold" style={{ fontSize: '14px' }}>
                    Uploads
                  </span>
                </div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  size="xs"
                  className={`csa-nav-expandable text-muted ${
                    uploadsExpanded ? 'csa-nav-expandable-expanded' : 'csa-nav-expandable-collapsed'
                  }`}
                />
              </div>
            </div>

            <div
              className={`csa-nav-submenu mt-1 ${
                uploadsExpanded ? 'csa-nav-submenu-expanded' : 'csa-nav-submenu-collapsed'
              }`}
            >
              <div className="csa-nav-submenu-inner">
                <div className="ps-2 border-start border-2 ms-2" style={{ borderColor: '#e5e7eb' }}>
                  <div
                    className={`csa-nav-subitem ${activeNavItem === 'audio-upload' ? 'active' : ''}`}
                    onClick={() => setActiveNavItem('audio-upload')}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faCloudArrowUp}
                        size="sm"
                        className={activeNavItem === 'audio-upload' ? 'text-primary' : 'text-muted'}
                      />
                      <span>Audio Upload</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-2">
            <div
              className={`csa-nav-item ${activeNavItem === 'deep-fake' ? 'active' : ''}`}
              onClick={() => setDimensionsExpanded(!dimensionsExpanded)}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faLayerGroup}
                    size="sm"
                    className={activeNavItem === 'deep-fake' ? 'text-primary' : 'text-muted'}
                  />
                  <span className="fw-semibold" style={{ fontSize: '14px' }}>
                    Dimensions
                  </span>
                </div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  size="xs"
                  className={`csa-nav-expandable text-muted ${
                    dimensionsExpanded ? 'csa-nav-expandable-expanded' : 'csa-nav-expandable-collapsed'
                  }`}
                />
              </div>
            </div>

            <div
              className={`csa-nav-submenu mt-1 ${
                dimensionsExpanded ? 'csa-nav-submenu-expanded' : 'csa-nav-submenu-collapsed'
              }`}
            >
              <div className="csa-nav-submenu-inner">
                <div className="ps-2 border-start border-2 ms-2" style={{ borderColor: '#e5e7eb' }}>
                  <div
                    className={`csa-nav-subitem ${activeNavItem === 'deep-fake' ? 'active' : ''}`}
                    onClick={() => setActiveNavItem('deep-fake')}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faMicrochip}
                        size="sm"
                        className={activeNavItem === 'deep-fake' ? 'text-primary' : 'text-muted'}
                      />
                      <span>Deep Fake</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </nav>
      </aside>

      <main
        style={{
          marginLeft: sidebarWidth,
          minHeight: '100vh'
        }}
      >
        <div
          className="csa-content-card"
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
            margin: '24px 24px 24px 16px',
            overflow: 'hidden',
            transition: 'box-shadow 0.2s ease'
          }}
        >
          <div
            style={{
              padding: '32px 32px 24px 32px',
              borderBottom: '1px solid #e9ecef'
            }}
          >
            <div className="d-flex align-items-start justify-content-between">
              <h2 className="mb-0 fw-bold" style={{ fontSize: '28px', color: '#212529' }}>
                {project.title || 'Untitled Project'}
              </h2>
              <div className="d-flex align-items-center gap-3 csa-details-actions">
                <Link
                  to={`/mtc+/update-project/${encodeURIComponent(projectId || '')}`}
                  className="csa-details-action csa-details-action-edit d-flex align-items-center gap-2"
                  onClick={e => {
                    e.preventDefault();
                    showYetToBeImplementedToast('Edit');
                  }}
                >
                  <FontAwesomeIcon icon={faPen} style={{ fontSize: '14px' }} />
                  Edit
                </Link>
                <button
                  type="button"
                  className="csa-details-action csa-details-action-delete d-flex align-items-center gap-2 border-0 bg-transparent"
                  onClick={() => showYetToBeImplementedToast('Delete')}
                >
                  <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: '14px' }} />
                  Delete
                </button>
              </div>
            </div>
            {project.domain && (
              <div className="d-flex align-items-center gap-2 text-muted mt-2" style={{ fontSize: '14px' }}>
                <FeatherIcon icon="briefcase" size={16} />
                <span>{getDomainDisplayName(project.domain)}</span>
              </div>
            )}
          </div>

          <div>{renderMainContent()}</div>
        </div>
      </main>
    </div>
  );
};

export default MTCPlusProjectDetails;
